import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { useShopMode } from "../context/ShopModeContext";
import { useAuth } from "../context/AuthContext";
import { mediaUrl, waLink } from "../lib/api";
import { toast } from "sonner";
import {
  ShoppingBag,
  X,
  Trash2,
  Plus,
  Minus,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Package
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Button } from "./ui/button";

export function CartDrawer() {
  const { cart, isCartOpen, closeCart, updateQuantity, removeFromCart } = useCart();
  const { settings } = useSettings();
  const { isWholesale } = useShopMode();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!isCartOpen) return null;

  const adminWhatsapp = settings?.support_whatsapp || settings?.whatsapp || settings?.socials?.whatsapp || "919082444611";

  const waWholesaleText =
    `*Wholesale Order Enquiry - ${settings?.brand_name || "Zaveri Trunk"}*\n\n` +
    `Hello Team, I would like to place a Wholesale Order for the following ${cart.count} item(s):\n\n` +
    cart.items.map((i, idx) => {
      const p = i.product || {};
      const code = p.code || (p.id ? `ZT-${p.id.slice(0, 6).toUpperCase()}` : "N/A");
      const cat = p.hierarchy_path || p.category_name || "Jewellery";
      const wt = p.weight_quintal ? ` | Wt: ${p.weight_quintal} Quintal` : "";
      const hasValidPrice = Boolean((p.price || p.retail_price) && Number(p.price || p.retail_price) > 0);
      const isProductLevelHide = p.price_display === "HIDE_AND_INQUIRE";
      const isProductLevelShow = p.price_display === "SHOW" || p.is_price_visible === true;
      const isStoreLevelShow = settings?.default_price_display === "SHOW" || settings?.wholesale_price_visibility === "show";
      const isPriceVisible = isWholesale
        ? (p.resolved_price_display === "SHOW" || isProductLevelShow || settings?.wholesale_price_visibility === "show")
        : (isProductLevelShow || isStoreLevelShow || (hasValidPrice && !isProductLevelHide && p.target_channel !== "b2b"));
      const rateText = isPriceVisible ? `₹${Number(p.price ?? p.retail_price).toLocaleString()} / ${p.unit || "piece"}` : "Quote on Inquiry";
      return `${idx + 1}. *${p.title || "Product"}*\n   • Code: ${code}\n   • Category: ${cat}\n   • Qty: ${i.quantity} ${p.unit || "piece"}(s)${wt}\n   • Rate: ${rateText}\n`;
    }).join("\n") +
    `\n--------------------------------\n` +
    `*Total Items:* ${cart.count}\n` +
    `*Total Estimated Value:* ${cart.subtotal > 0 && !isWholesale ? `₹${Number(cart.subtotal).toLocaleString()}` : "Quote on Inquiry"}\n` +
    (user ? `\n*Buyer Details:*\n• Name: ${user.name || "Wholesale Merchant"}\n• Phone: ${user.whatsapp || ""}\n• Business: ${user.business_name || "N/A"}${user.gstin ? `\n• GSTIN: ${user.gstin}` : ""}` : "") +
    `\n--------------------------------\n` +
    `Please confirm stock availability, wholesale invoice, and dispatch schedule. Thank you!`;

  const handleWholesaleCheckout = () => {
    const url = waLink(adminWhatsapp, waWholesaleText);
    window.open(url, "_blank");
    toast.success("Opening WhatsApp with your wholesale cart items...");
  };

  const hasOutOfStockItems = cart.items.some((i) => {
    const p = i.product || {};
    return p.is_out_of_stock || p.resolved_stock_status === "out_of_stock" || (typeof p.stock_quantity === "number" && p.stock_quantity <= 0);
  });

  const handleCheckout = () => {
    if (hasOutOfStockItems) {
      toast.error("Please remove out-of-stock items before proceeding to checkout.");
      return;
    }
    closeCart();
    if (!user) {
      navigate("/signup");
      return;
    }
    navigate("/checkout");
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end overflow-hidden" data-testid="cart-drawer">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity cursor-pointer"
        onClick={closeCart}
      />

      {/* Drawer Panel */}
      <div className="relative z-10 w-full sm:w-[440px] max-w-full bg-white text-stone-900 shadow-2xl flex flex-col h-[100dvh] max-h-[100dvh]">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-stone-800 flex items-center justify-between bg-stone-900 text-white shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
              <ShoppingBag className="w-4.5 h-4.5 text-[#D4AF37]" />
            </div>
            <div>
              <h2 className="font-serif-display text-base sm:text-lg font-bold tracking-tight leading-tight">
                {isWholesale ? "Wholesale Cart" : "Shopping Cart"}
              </h2>
              <span className="text-[11px] text-stone-400">
                {cart.count} {cart.count === 1 ? "item" : "items"}
              </span>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="w-8 h-8 rounded-full flex items-center justify-center text-stone-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items List */}
        <div className="flex-1 overflow-y-auto p-3.5 sm:p-5 space-y-3 sm:space-y-4 overscroll-contain">
          {hasOutOfStockItems && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-800 flex items-start gap-2">
              <span className="font-bold text-red-600">⚠️</span>
              <div>
                <strong className="font-bold">Item(s) Out of Stock:</strong> Some items in your cart are currently out of stock. Please remove them to proceed.
              </div>
            </div>
          )}

          {cart.items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-6 space-y-4">
              <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center text-stone-400">
                <ShoppingBag className="w-8 h-8" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 text-base">Your Cart is Empty</h3>
                <p className="text-xs text-stone-500 mt-1 max-w-xs">
                  {isWholesale
                    ? "Explore our luxury wholesale jewellery collection and add items to your quote cart."
                    : "Explore our luxury jewellery collection and add items to your cart."}
                </p>
              </div>
              <Button
                onClick={() => { closeCart(); navigate("/products"); }}
                className="bg-[#B8860B] hover:bg-[#9a7009] text-white rounded-full text-xs px-6 py-2"
              >
                Browse Catalogue
              </Button>
            </div>
          ) : (
            cart.items.map((item) => {
              const prod = item.product || {};
              const cover = prod.media?.[0];
              const hasItemPrice = Boolean((prod.price || prod.retail_price) && Number(prod.price || prod.retail_price) > 0);
              const isProductLevelHide = prod.price_display === "HIDE_AND_INQUIRE";
              const isProductLevelShow = prod.price_display === "SHOW" || prod.is_price_visible === true;
              const isStoreLevelShow = settings?.default_price_display === "SHOW" || settings?.wholesale_price_visibility === "show";
              
              const isItemPriceVisible = isWholesale
                ? (prod.resolved_price_display === "SHOW" || isProductLevelShow || settings?.wholesale_price_visibility === "show")
                : (isProductLevelShow || isStoreLevelShow || (hasItemPrice && !isProductLevelHide && prod.target_channel !== "b2b"));

              const isItemOutOfStock = Boolean(
                prod.is_out_of_stock ||
                prod.resolved_stock_status === "out_of_stock" ||
                (typeof prod.stock_quantity === "number" && prod.stock_quantity <= 0)
              );

              const effectiveItemMax = (typeof prod.stock_quantity === "number" && prod.stock_quantity > 0)
                ? (prod.max_order_quantity ? Math.min(prod.max_order_quantity, prod.stock_quantity) : prod.stock_quantity)
                : prod.max_order_quantity;

              const itemUnitPrice = Number(prod.price ?? prod.retail_price ?? 0);
              const itemTotal = Number(item.item_total || (itemUnitPrice * item.quantity));

              return (
                <div
                  key={item.product_id}
                  className={`flex gap-3 p-3 rounded-2xl relative group min-w-0 transition-all border ${
                    isItemOutOfStock
                      ? "bg-red-50/50 border-red-200 opacity-90"
                      : "bg-stone-50/80 hover:bg-stone-50 border-stone-200/90"
                  }`}
                >
                  <div className="w-20 h-20 bg-stone-100 rounded-xl overflow-hidden shrink-0 border border-stone-200/70 relative">
                    {cover?.url ? (
                      <img
                        src={mediaUrl(cover.url)}
                        alt={prod.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-300">
                        <Package className="w-6 h-6" />
                      </div>
                    )}
                    {isItemOutOfStock && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-center p-1">
                        <span className="text-[9px] font-bold text-red-200 uppercase leading-tight">Out of Stock</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      <div className="flex items-start justify-between gap-1.5">
                        <div className="flex items-center gap-1.5 truncate">
                          <span className="text-[9px] uppercase font-bold text-[#B8860B] tracking-wider block truncate">
                            {prod.hierarchy_path || prod.category_name || "Jewellery"}
                          </span>
                          {isItemOutOfStock && (
                            <span className="text-[9px] font-bold text-red-700 bg-red-100 border border-red-300 px-1.5 py-0.2 rounded shrink-0">
                              Out of Stock
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          className="text-stone-400 hover:text-red-500 p-1 -mr-1 -mt-1 rounded-md hover:bg-red-50 transition-colors shrink-0 cursor-pointer"
                          title="Remove item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <h4 className="font-semibold text-stone-900 text-xs sm:text-sm line-clamp-2 leading-snug">
                        {prod.title || "Jewellery Product"}
                      </h4>
                      {isItemPriceVisible ? (
                        <p className="text-xs sm:text-sm font-bold text-[#B8860B] mt-1">
                          ₹{itemUnitPrice.toLocaleString()} <span className="text-[10px] sm:text-xs text-stone-500 font-normal">/ {prod.unit || "Piece"}</span>
                        </p>
                      ) : (
                        <p className="text-xs font-semibold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block mt-1">
                          Quote on Inquiry <span className="text-[10px] text-stone-500 font-normal">/ {prod.unit || "Piece"}</span>
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-stone-200/60">
                      {/* Quantity Selector with Stock & Max Limit Badge */}
                      {(() => {
                        const isMaxReached = Boolean(effectiveItemMax && item.quantity >= effectiveItemMax);
                        return (
                          <div className="flex items-center gap-1.5">
                            <div className="flex items-center border border-stone-300/80 bg-white rounded-lg shadow-2xs h-7">
                              <button
                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                className="w-7 h-full flex items-center justify-center text-stone-600 hover:bg-stone-100 rounded-l-lg transition-colors cursor-pointer"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-2.5 text-xs font-bold text-stone-900 min-w-[24px] text-center">
                                {item.quantity}
                              </span>
                              <button
                                disabled={isMaxReached || isItemOutOfStock}
                                onClick={() => {
                                  if (!isMaxReached && !isItemOutOfStock) {
                                    updateQuantity(item.product_id, item.quantity + 1);
                                  }
                                }}
                                className="w-7 h-full flex items-center justify-center text-stone-600 hover:bg-stone-100 rounded-r-lg transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Increase quantity"
                                title={isMaxReached ? `Limit (${effectiveItemMax}) reached` : "Increase quantity"}
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            {effectiveItemMax && (
                              <span
                                className="text-[10px] font-bold text-amber-900 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded shrink-0"
                                title={`Limit: ${effectiveItemMax} units`}
                              >
                                Max: {effectiveItemMax}
                              </span>
                            )}
                          </div>
                        );
                      })()}

                      {isItemPriceVisible ? (
                        <span className="font-bold text-stone-900 text-sm sm:text-base">
                          ₹{itemTotal.toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-stone-500 text-xs font-medium italic">Quote Req.</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer Subtotal & Proceed to Buy */}
        {cart.items.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-stone-200 bg-stone-50/95 backdrop-blur-xs space-y-3 shrink-0 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] pb-[max(1rem,env(safe-area-inset-bottom))]">
            <div className="space-y-1.5 text-xs text-stone-600">
              <div className="flex justify-between items-center">
                <span>Subtotal ({cart.count} {cart.count === 1 ? "item" : "items"})</span>
                <span className="font-semibold text-stone-900 text-xs sm:text-sm">
                  {isWholesale ? "Quote on Request" : (cart.subtotal > 0 ? `₹${Number(cart.subtotal).toLocaleString()}` : "Quote on Request")}
                </span>
              </div>
              <div className="flex justify-between items-baseline text-sm sm:text-base font-bold text-stone-900 pt-2 border-t border-stone-200">
                <span>Total Amount</span>
                <span className="text-[#B8860B] text-base sm:text-lg">
                  {isWholesale ? "Quote on Request" : (cart.subtotal > 0 ? `₹${Number(cart.subtotal).toLocaleString()}` : "Quote on Request")}
                </span>
              </div>
            </div>

            {isWholesale ? (
              /* WHOLESALE CHECKOUT: Primary action sends all cart items to WhatsApp */
              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={handleWholesaleCheckout}
                  data-testid="cart-wholesale-whatsapp-btn"
                  className="w-full bg-[#25D366] hover:bg-[#20bd5a] active:scale-[0.99] text-white rounded-full font-bold text-sm py-3.5 shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <FaWhatsapp className="w-4.5 h-4.5 text-white" />
                  <span>Checkout via WhatsApp</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    closeCart();
                    if (!user) {
                      navigate("/signup");
                      return;
                    }
                    navigate("/checkout");
                  }}
                  data-testid="cart-wholesale-review-btn"
                  className="w-full bg-stone-100 hover:bg-stone-200 active:scale-[0.99] text-stone-700 rounded-full font-semibold text-xs py-2.5 transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <span>Review Order Details at Checkout</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              /* RETAIL CHECKOUT: Standard online checkout */
              <Button
                onClick={handleCheckout}
                data-testid="cart-checkout-btn"
                className="w-full bg-[#B8860B] hover:bg-[#9a7009] active:scale-[0.99] text-white rounded-full font-bold text-sm sm:text-base py-3.5 sm:py-4 shadow-md transition-all flex items-center justify-center gap-2 mt-2 cursor-pointer"
              >
                Proceed to Checkout <ArrowRight className="w-4 h-4" />
              </Button>
            )}

            <p className="text-[11px] text-center text-stone-400 flex items-center justify-center gap-1.5 pt-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
              <span>{isWholesale ? "100% Insured Delivery & B2B GST Invoice" : "100% Insured Delivery & Secure Checkout"}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
